import React, { useState, useEffect } from "react";
import { miniGameService } from "../../services/miniGameService";
import { toast } from "react-toastify";
import ClipLoader from "react-spinners/ClipLoader"; // For spinner

const StaffGameSettings = () => {
  const [settings, setSettings] = useState({
    startDate: "",
    endDate: "",
    cooldownHours: "",
    winProbability: "",
    questionsPerQuiz: "",
  });
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false); // For update spinner

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await miniGameService.fetchGameSettings();
      const data = response.data;
      setSettings({
        startDate: data.startDate.split("T")[0],
        endDate: data.endDate.split("T")[0],
        cooldownHours: data.cooldownHours,
        winProbability: data.winProbability * 100,
        questionsPerQuiz: data.questionsPerQuiz,
      });
    } catch (err) {
      toast.error("Không thể tải cài đặt trò chơi.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  const validateSettings = () => {
    const {
      startDate,
      endDate,
      cooldownHours,
      winProbability,
      questionsPerQuiz,
    } = settings;
    const now = new Date().toISOString().split("T")[0];
    if (
      !startDate ||
      !endDate ||
      !cooldownHours ||
      !winProbability ||
      !questionsPerQuiz
    ) {
      toast.error("Vui lòng điền đầy đủ tất cả các trường.");
      return false;
    }
    if (startDate < now) {
      toast.error("Ngày bắt đầu phải ở tương lai.");
      return false;
    }
    if (endDate <= startDate) {
      toast.error("Ngày kết thúc phải sau ngày bắt đầu.");
      return false;
    }
    if (parseInt(cooldownHours) < 0) {
      toast.error("Thời gian chờ phải không âm.");
      return false;
    }
    const winProb = parseFloat(winProbability);
    if (winProb < 0 || winProb > 100) {
      toast.error("Xác suất thắng phải từ 0 đến 100%.");
      return false;
    }
    if (parseInt(questionsPerQuiz) < 1) {
      toast.error("Số câu hỏi phải ít nhất là 1.");
      return false;
    }
    return true;
  };

  const updateSettings = async () => {
    if (!validateSettings()) return;
    setIsUpdating(true);
    try {
      const payload = {
        id: 1,
        startDate: settings.startDate,
        endDate: settings.endDate,
        cooldownHours: parseInt(settings.cooldownHours),
        winProbability: parseFloat(settings.winProbability) / 100,
        questionsPerQuiz: parseInt(settings.questionsPerQuiz),
      };
      await miniGameService.updateSettings(payload);
      toast.success("Cập nhật cài đặt trò chơi thành công!");
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || "Lỗi khi cập nhật cài đặt trò chơi.";
      toast.error(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <ClipLoader size={50} color="#2563eb" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">
        Cài đặt trò chơi đố vui
      </h2>
      <div className="bg-white p-6 rounded-xl shadow-md">
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Ngày bắt đầu
            </label>
            <input
              type="date"
              name="startDate"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={settings.startDate}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Ngày kết thúc
            </label>
            <input
              type="date"
              name="endDate"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={settings.endDate}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Thời gian chờ (giờ)
            </label>
            <input
              type="number"
              name="cooldownHours"
              min="0"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={settings.cooldownHours}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Xác suất thắng (%)
            </label>
            <input
              type="number"
              name="winProbability"
              min="0"
              max="100"
              step="0.1"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={settings.winProbability}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Số câu hỏi mỗi lượt
            </label>
            <input
              type="number"
              name="questionsPerQuiz"
              min="1"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={settings.questionsPerQuiz}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <button
          className="mt-6 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 active:bg-blue-800 flex items-center justify-center"
          onClick={updateSettings}
          disabled={isUpdating}
        >
          {isUpdating ? (
            <>
              <ClipLoader size={20} color="#ffffff" className="mr-2" />
              Đang cập nhật...
            </>
          ) : (
            "Cập nhật cài đặt"
          )}
        </button>
      </div>
    </div>
  );
};

export default StaffGameSettings;
